import { useState, useEffect, useCallback, useRef } from 'react';
import type { IntegrationAddress } from 'src/api/generated';
import { useWhatsAppPhones } from 'src/modules/shared/hooks/api/messaging/whatsapp/phones';
import { standardizePhoneNumber } from 'src/modules/shared/utils/formatting/phone.util';
import { normalizeWhatsAppPhoneNumber } from 'src/modules/shared/utils/formatting/whatsapp.util';

interface UseSelectedPhoneProps {
  /** Stable provider ID; preferred whenever present. */
  initialPhoneId?: string;
  /** Sender-address fallback used only when no provider ID exists. */
  initialPhoneAddress?: string;
}

interface UseSelectedPhoneReturn {
  /** Connected sender options returned by the API. */
  phones: IntegrationAddress[];
  /** Whether connected sender options are loading. */
  loading: boolean;
  /** Connected sender lookup failure, when present. */
  error: Error | null;
  /** Persisted sender ID, even when a refresh no longer returns that sender. */
  selectedPhoneId: string | undefined;
  /** Selected connected sender, or undefined while its persisted ID is unavailable. */
  selectedPhone: IntegrationAddress | undefined;
  /** Applies an explicit sender selection from the available options. */
  handlePhoneChange: (phoneId?: string) => void;
}

/**
 * Selects connected senders using the shared WhatsApp identity normalization.
 * @param props - Initial provider ID or legacy composer address to preserve.
 * @returns Connected choices and the explicit selection, including stale IDs.
 */
export const useSelectedPhone = ({
  initialPhoneId,
  initialPhoneAddress,
}: UseSelectedPhoneProps): UseSelectedPhoneReturn => {
  const { phones, loading, error } = useWhatsAppPhones();

  const [selectedPhoneId, setSelectedPhoneId] = useState<string | undefined>();
  const initialSelectionKey = `${initialPhoneId ?? ''}\u0000${
    initialPhoneAddress ?? ''
  }`;
  const previousInitialSelectionKey = useRef(initialSelectionKey);

  useEffect(() => {
    const initialSelectionChanged =
      previousInitialSelectionKey.current !== initialSelectionKey;
    previousInitialSelectionKey.current = initialSelectionKey;

    setSelectedPhoneId((currentPhoneId) => {
      if (!initialSelectionChanged && currentPhoneId) {
        return currentPhoneId;
      }
      if (initialPhoneId) {
        return initialPhoneId;
      }
      const normalized = normalizeWhatsAppPhoneNumber(
        initialPhoneAddress ?? ''
      );
      return phones.find(
        (phone) =>
          Boolean(normalized) &&
          normalizeWhatsAppPhoneNumber(phone.address) === normalized
      )?.providerId;
    });
  }, [initialPhoneAddress, initialPhoneId, initialSelectionKey, phones]);

  const selectedPhone = phones.find(
    (phone) => phone.providerId === selectedPhoneId
  );

  const handlePhoneChange = (phoneId?: string) => {
    setSelectedPhoneId(
      phones.some((phone) => phone.providerId === phoneId) ? phoneId : undefined
    );
  };

  return {
    phones,
    loading,
    error,
    selectedPhoneId,
    selectedPhone,
    handlePhoneChange,
  };
};

/**
 * Props for the usePhoneNumberInput hook
 */
interface UsePhoneNumberInputProps {
  /** The initial phone number value */
  initialNumber: string;
}

/**
 * Return type for the usePhoneNumberInput hook
 */
interface UsePhoneNumberInputReturn {
  /** The current phone number value */
  phoneNumber: string;
  /** Whether the current phone number is valid */
  isValid: boolean;
  /** Debounced validity that flips only after the user stops typing */
  isDebouncedValid: boolean;
  /** Handler for input change events */
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Function to programmatically set the phone number */
  setPhoneNumber: (number: string) => void;
}

export const usePhoneNumberInput = ({
  initialNumber,
}: UsePhoneNumberInputProps): UsePhoneNumberInputReturn => {
  const [phoneNumber, setPhoneNumberState] = useState(initialNumber);
  const [isValid, setIsValid] = useState<boolean>(() =>
    Boolean(standardizePhoneNumber(initialNumber))
  );
  const [isDebouncedValid, setIsDebouncedValid] = useState<boolean>(() =>
    Boolean(standardizePhoneNumber(initialNumber))
  );

  const setPhoneNumber = useCallback((number: string) => {
    const standardizedNumber = standardizePhoneNumber(number);
    const isValid = !!standardizedNumber;

    setIsValid(isValid);
    setIsDebouncedValid(true);

    setPhoneNumberState(number);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const number = e.target.value;
    setPhoneNumber(number);
  };

  useEffect(() => {
    const timerId = setTimeout(() => {
      setIsDebouncedValid(isValid);
    }, 600);

    return () => clearTimeout(timerId);
  }, [isValid, phoneNumber]);

  return {
    phoneNumber,
    isValid,
    isDebouncedValid,
    handleChange,
    setPhoneNumber,
  };
};
